import type { ChangeEvent, FC } from "react";
import { useCallback, useId, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useForm } from "react-hook-form";
import useFormPersist from "react-hook-form-persist";
import TextareaAutosize from "react-textarea-autosize";
import { twMerge } from "tailwind-merge";

import { AttachmentChip } from "./AttachmentChip";
import { MentionSuggestions } from "./MentionSuggestions";
import {
  type MentionTarget,
  filterMentionTargets,
  getActiveMentionQuery,
  replaceActiveMentionQuery,
} from "./mentions";

import { collaborationUploadFile } from "api/api-worksheets";
import { AIServerFileType } from "components/server-types";
import { t } from "i18n/i18n";
import { SquareIconButton } from "mds/components/buttons/SquareIconButton";
import { ArrowRightIcon, PaperClipLinkIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { toastWarningMessage } from "utils/alerts";
import { rollbarAndLogError } from "utils/logger";

const ACCEPTED_AI_FILE_TYPES = {
  "image/png": [],
  "image/jpeg": [],
  "image/gif": [],
  "image/webp": [],
  "application/pdf": [],
};

const MAX_FILE_SIZE_MB = 5;

type PendingFile = {
  id: string;
  file: File;
  url?: string;
  uploading: boolean;
};

interface FormInput {
  text: string;
}

export type ChatComposeProps = {
  className?: string;
  mentionTargets?: MentionTarget[];
  onSend?: (text: string, files?: AIServerFileType[]) => void;
  placeholder?: string;
  /**
   * Optional key for form persistence.
   * If provided, the form state will be saved to localStorage under this key.
   * Like controlled/uncontrolled components, this should NEVER change during the component's lifetime.
   */
  persistKey?: string;
  /**
   * Context in which the chat compose is being used.
   * - "class": Used in class chat, requires canCreateChatMessage capability
   * - "ai": Used in AI chat, always allows sending messages
   */
  context?: "class" | "ai";
};

let nextFileId = 0;
const EMPTY_MENTION_TARGETS: MentionTarget[] = [];

const getNextFileId = () => {
  nextFileId += 1;
  return String(nextFileId);
};

/*
 * Compose component for the chat.
 * Note that this is used for both the class chat and the AI chat,
 * which have different requirements.
 * Any user who has access to the AI chat should be able to send messages,
 * whereas we require a capabilities check for the class chat.
 */
export const ChatCompose: FC<ChatComposeProps> = ({
  className,
  mentionTargets = EMPTY_MENTION_TARGETS,
  onSend = () => {},
  placeholder,
  persistKey,
  context = "class",
}) => {
  const { canCreateChatMessage } = useCapabilities();
  const isAIPanel = context === "ai";
  const canSendMessage = isAIPanel || canCreateChatMessage;

  const formRef = useRef<HTMLFormElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const mentionListId = useId();
  const { register, handleSubmit, watch, setValue, reset } = useForm<FormInput>();
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [selection, setSelection] = useState({ startOffset: 0, endOffset: 0 });
  const [activeMentionIndex, setActiveMentionIndex] = useState(0);
  const [mentionSuggestionsDismissed, setMentionSuggestionsDismissed] = useState(false);

  let clearPersisted = () => {};
  // `persistKey` MUST be a stable string that does not change during the component's lifetime.
  if (persistKey) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const actions = useFormPersist(persistKey, {
      watch,
      setValue,
      storage: window.localStorage,
    });
    clearPersisted = actions.clear;
  }

  const uploadFile = useCallback(async (file: File) => {
    const id = getNextFileId();
    const pendingFile: PendingFile = { id, file, uploading: true };
    setPendingFiles((prev) => [...prev, pendingFile]);

    try {
      const { url } = await collaborationUploadFile(file);
      setPendingFiles((prev) =>
        prev.map((f) => (f.id === id ? { ...f, url, uploading: false } : f)),
      );
    } catch (err) {
      rollbarAndLogError("AI chat file upload failed", err);
      setPendingFiles((prev) => prev.filter((f) => f.id !== id));
      toastWarningMessage(t("chat.upload_failed"));
    }
  }, []);

  const handleFileDrop = useCallback(
    (acceptedFiles: File[]) => {
      for (const file of acceptedFiles) {
        if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
          toastWarningMessage(t("chat.file_too_large", { maxSize: MAX_FILE_SIZE_MB }));
        } else {
          uploadFile(file);
        }
      }
    },
    [uploadFile],
  );

  const removePendingFile = useCallback((id: string) => {
    setPendingFiles((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: ACCEPTED_AI_FILE_TYPES,
    multiple: true,
    noClick: true,
    noKeyboard: true,
    onDrop: handleFileDrop,
    onDropRejected: () => {
      toastWarningMessage(t("chat.unsupported_file_type"));
    },
  });

  const readyFiles = pendingFiles.filter((f) => f.url);
  const hasUploading = pendingFiles.some((f) => f.uploading);

  const onSubmit = handleSubmit(({ text }) => {
    const files: AIServerFileType[] = readyFiles.map((f) => ({
      url: f.url!,
      content_type: f.file.type,
      name: f.file.name,
    }));
    onSend(text, files.length > 0 ? files : undefined);
    setPendingFiles([]);
    clearPersisted();
    reset();
    setSelection({ startOffset: 0, endOffset: 0 });
    setActiveMentionIndex(0);
    setMentionSuggestionsDismissed(false);
  });

  const text = watch("text", "");
  const canSubmit = (text !== "" || readyFiles.length > 0) && !hasUploading;
  const mentionsEnabled = !isAIPanel && mentionTargets.length > 0;
  const activeMentionQuery = mentionsEnabled
    ? getActiveMentionQuery(text, selection.startOffset, selection.endOffset)
    : null;
  const mentionSuggestions =
    !mentionSuggestionsDismissed && activeMentionQuery
      ? filterMentionTargets(mentionTargets, activeMentionQuery.query)
      : [];
  const showMentionSuggestions = mentionSuggestions.length > 0;
  const safeActiveMentionIndex = Math.min(
    activeMentionIndex,
    Math.max(mentionSuggestions.length - 1, 0),
  );

  const selectMention = useCallback(
    (target: MentionTarget) => {
      if (!activeMentionQuery) return;

      const replacement = replaceActiveMentionQuery(text, activeMentionQuery, target.mentionText);
      setValue("text", replacement.text, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      setSelection({
        startOffset: replacement.cursorPosition,
        endOffset: replacement.cursorPosition,
      });
      setActiveMentionIndex(0);
      setMentionSuggestionsDismissed(true);

      window.setTimeout(() => {
        textAreaRef.current?.focus();
        textAreaRef.current?.setSelectionRange(
          replacement.cursorPosition,
          replacement.cursorPosition,
        );
      }, 0);
    },
    [activeMentionQuery, setValue, text],
  );

  const dropzoneProps = isAIPanel ? getRootProps() : {};
  const textRegistration = register("text", {
    required: pendingFiles.length === 0,
    disabled: !canSendMessage,
    onBlur() {
      setMentionSuggestionsDismissed(true);
    },
    onChange(event: ChangeEvent<HTMLTextAreaElement>) {
      setSelection({
        startOffset: event.currentTarget.selectionStart,
        endOffset: event.currentTarget.selectionEnd,
      });
      setActiveMentionIndex(0);
      setMentionSuggestionsDismissed(false);
    },
  });

  return (
    <div
      {...dropzoneProps}
      className={twMerge(
        "relative flex w-full flex-col rounded-lg border border-black-tint-70 p-1",
        isDragActive && isAIPanel && "border-blue bg-blue-tint-95",
        className,
      )}
    >
      {isAIPanel && <input {...getInputProps()} ref={fileInputRef} />}

      {pendingFiles.length > 0 && (
        <div className="flex flex-wrap gap-1.5 px-2 pb-1 pt-1.5">
          {pendingFiles.map((pf) => (
            <AttachmentChip
              file={pf.file}
              key={pf.id}
              name={pf.file.name}
              uploading={pf.uploading}
              onRemove={() => removePendingFile(pf.id)}
            />
          ))}
        </div>
      )}

      {showMentionSuggestions && (
        <MentionSuggestions
          activeIndex={safeActiveMentionIndex}
          id={mentionListId}
          targets={mentionSuggestions}
          onSelect={selectMention}
        />
      )}
      <span aria-atomic="true" aria-live="polite" className="sr-only">
        {showMentionSuggestions ? t("chat.mention_suggestions_available") : ""}
      </span>

      <form
        className="flex w-full items-center justify-center gap-1"
        ref={formRef}
        onSubmit={onSubmit}
      >
        <TextareaAutosize
          {...textRegistration}
          aria-activedescendant={
            showMentionSuggestions ? `${mentionListId}-option-${safeActiveMentionIndex}` : undefined
          }
          aria-autocomplete={mentionsEnabled ? "list" : undefined}
          aria-controls={showMentionSuggestions ? mentionListId : undefined}
          aria-expanded={mentionsEnabled ? showMentionSuggestions : undefined}
          aria-label={t("chat.message_input")}
          className={twMerge("ml-2 grow resize-none", !canSendMessage && "italic")}
          maxRows={5}
          minRows={1}
          placeholder={canSendMessage ? placeholder : t("chat.join_class_to_chat")}
          ref={(element) => {
            textRegistration.ref(element);
            textAreaRef.current = element;
          }}
          wrap="soft"
          onKeyDown={(event) => {
            if (showMentionSuggestions) {
              if (event.key === "ArrowUp") {
                event.preventDefault();
                setActiveMentionIndex(
                  safeActiveMentionIndex === 0
                    ? mentionSuggestions.length - 1
                    : safeActiveMentionIndex - 1,
                );
                return;
              }
              if (event.key === "ArrowDown") {
                event.preventDefault();
                setActiveMentionIndex(
                  safeActiveMentionIndex === mentionSuggestions.length - 1
                    ? 0
                    : safeActiveMentionIndex + 1,
                );
                return;
              }
              if (event.key === "Enter" || event.key === "Tab") {
                event.preventDefault();
                selectMention(mentionSuggestions[safeActiveMentionIndex]);
                return;
              }
              if (event.key === "Escape") {
                event.preventDefault();
                setMentionSuggestionsDismissed(true);
                return;
              }
            }

            if (event.key === "Enter" && !event.shiftKey) {
              formRef.current?.requestSubmit();
              event.preventDefault();
            }
          }}
          onSelect={(event) => {
            setSelection({
              startOffset: event.currentTarget.selectionStart,
              endOffset: event.currentTarget.selectionEnd,
            });
          }}
        />
        {isAIPanel && (
          <SquareIconButton
            aria-label={t("chat.attach_file")}
            kind="ghost"
            size="l"
            type="button"
            onClick={() => fileInputRef.current?.click()}
          >
            <PaperClipLinkIcon />
          </SquareIconButton>
        )}
        <SquareIconButton
          aria-label={t("chat.send_message")}
          disabled={!canSubmit}
          kind="secondary"
          size="l"
          type="submit"
        >
          <ArrowRightIcon />
        </SquareIconButton>
      </form>
    </div>
  );
};
